import { Order } from '@datorama/akita';

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export declare type SortFunction<E, S extends { ui: { sortDirection: SortDirection } }> = (a: E, b: E, state?: S) => number;
export type SortCtx<T> = { ui: { sortDirection: SortDirection; valueGetterFn?: (data: T) => string } };

export function naturalSort<T, S extends SortCtx<T>>(key: keyof T) {
  return (a: T, b: T, state: S) => {
    const options: Intl.CollatorOptions = {
      numeric: true,
      sensitivity: 'base',
    };
    const firstComparator = (a[key] as unknown as string)?.toString() || '';
    const secondComparator = (b[key] as unknown as string)?.toString() || '';

    if (!state.ui.sortDirection) {
      return 0;
    }

    return state.ui.sortDirection === SortDirection.ASC
      ? firstComparator.localeCompare(secondComparator, undefined, options)
      : secondComparator.localeCompare(firstComparator, undefined, options);
  };
}

export function multipleSort<T, S extends SortCtx<T>>(...comparators: Array<(a: T, b: T, state: S) => number>) {
  return (a: T, b: T, state: S): number => {
    for (const comparator of comparators) {
      const result = comparator(a, b, state);
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  };
}

export function arrayNaturalSort<T>(arrayToBeSorted: T[], key: keyof T, sortDirection?: SortDirection): T[] {
  const options: Intl.CollatorOptions = {
    numeric: true,
    sensitivity: 'base',
  };

  return [...arrayToBeSorted].sort((a, b) => {
    if (!sortDirection) {
      return 0;
    }

    return sortDirection === SortDirection.ASC
      ? (a[key] || '').toString().localeCompare((b[key] || '').toString(), undefined, options)
      : (b[key] || '').toString().localeCompare((a[key] || '').toString(), undefined, options);
  });
}

export function valueGetterFnSort<T, S extends SortCtx<T>>() {
  return (a: T, b: T, state: S) => {
    const options: Intl.CollatorOptions = {
      numeric: true,
      sensitivity: 'base',
    };
    if (!state.ui.sortDirection || !state.ui.valueGetterFn) {
      return 0;
    }

    const firstComparator = (state.ui.valueGetterFn(a) as unknown as string) || '';
    const secondComparator = (state.ui.valueGetterFn(b) as unknown as string) || '';
    return state.ui.sortDirection === SortDirection.ASC
      ? firstComparator.toString().localeCompare(secondComparator, undefined, options)
      : secondComparator.toString().localeCompare(firstComparator, undefined, options);
  };
}

export function currencyGetterFnSort<T, S extends SortCtx<T>>() {
  return (a: T, b: T, state: S) => {
    if (!state.ui.sortDirection || !state.ui.valueGetterFn) return 0;

    const parseCurrency = (val: string): number | null => {
      if (!val || val.trim() === '') return null;

      val = val.trim();
      const isNegative = val.startsWith('-');
      val = val.replace(/[^0-9.,]/g, '');

      const lastComma = val.lastIndexOf(',');
      const lastDot = val.lastIndexOf('.');

      let num: number;

      if (lastComma > lastDot) {
        num = parseFloat(val.replace(/\./g, '').replace(',', '.'));
      } else {
        num = parseFloat(val.replace(/,/g, ''));
      }

      if (isNaN(num)) return null;

      return isNegative ? -Math.abs(num) : num;
    };

    const valA = parseCurrency(state.ui.valueGetterFn(a) ?? '');
    const valB = parseCurrency(state.ui.valueGetterFn(b) ?? '');

    if (valA === null && valB === null) return 0;
    if (valA === null) return 1;
    if (valB === null) return -1;

    return state.ui.sortDirection === SortDirection.ASC ? valA - valB : valB - valA;
  };
}

export function compareValues<T, K extends keyof T>(key: K, order = Order.ASC) {
  return function (a: T, b: T) {
    const varA = typeof a[key] === 'string' ? (a[key] as string).toUpperCase() : a[key];
    const varB = typeof b[key] === 'string' ? (b[key] as string).toUpperCase() : b[key];
    let comparison = 0;
    if (varA > varB) {
      comparison = 1;
    } else if (varA < varB) {
      comparison = -1;
    }
    return order == Order.DESC ? comparison * -1 : comparison;
  };
}

export function booleanSort<T, S extends SortCtx<T>>(key: keyof T) {
  return (a: T, b: T, state: S) => {
    if (!state.ui.sortDirection) {
      return 0;
    }

    const valA = a[key] as unknown as boolean;
    const valB = b[key] as unknown as boolean;

    if (valA === valB) return 0;
    if (valA && !valB) return state.ui.sortDirection === SortDirection.ASC ? -1 : 1;
    if (!valA && valB) return state.ui.sortDirection === SortDirection.ASC ? 1 : -1;

    return 0;
  };
}
